import React from 'react';
import { useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { GET_INTEGRATION_SETTINGS, GET_INTEGRATION_SETTINGS_VALUES_LIST } from '../api';
import { groupBy, keyBy } from 'utils/arrayUtils';
import {
  getIntegrationSettingsDialogFormData,
  getLocationIntegrationSettingsTableData,
  getUserIntegrationSettingsTableData,
  getAccountIntegrationSettingsTableData,
} from '../lib';
import {
  IntegrationSettingsForm,
  IntegrationSettingsLevel,
  IntegrationSettingsTabs,
  IntegrationSettingsUserLevelForm,
} from '../types';
import { TabsOption } from 'global';
import { UserProfile } from 'types';
import { IntegrationSettingsValues, Location } from '@trnsact/trnsact-shared-types';

export function useFetchIntegrationSettingsDialogData(
  integrationName: string,
  dealerAccountId: string,
  dealerAccountName: string,
  dealerContacts: UserProfile[],
  dealerLocations: Location[],
  tabComponentByLevel: Record<
    IntegrationSettingsTabs,
    React.ComponentType<{
      integrationSettings: any;
      tableData: IntegrationSettingsUserLevelForm | any;
      vendorProfile?: any;
    }>
  >,
  vendorProfile?: any
) {
  const [tabs, setTabs] = useState<TabsOption<IntegrationSettingsTabs>[]>([]);
  const [formData, setFormData] = useState<IntegrationSettingsForm>();

  const { data: integrationSettingsValuesListData, loading: isISVLoading } = useQuery(
    GET_INTEGRATION_SETTINGS_VALUES_LIST,
    {
      skip: !integrationName,
      variables: { input: { integrationName, accountId: dealerAccountId } },
      fetchPolicy: 'no-cache',
      context: { authRequired: true },
    }
  );

  const { data: integrationSettingsData, loading: isISLoading } = useQuery(GET_INTEGRATION_SETTINGS, {
    skip: !integrationName,
    variables: { integrationName },
    fetchPolicy: 'no-cache',
    context: { authRequired: true },
  });

  useEffect(() => {
    if (
      !integrationSettingsData?.integrationSettings ||
      !integrationSettingsValuesListData?.integrationSettingsValuesList
    ) {
      return;
    }
    const { integrationSettings } = integrationSettingsData;
    const { integrationSettingsValuesList } = integrationSettingsValuesListData;

    const integrationSettingsValuesListByUser = keyBy(integrationSettingsValuesList, 'userProfileId') as Record<
      string,
      IntegrationSettingsValues
    >;
    const integrationSettingsValuesListByLocation = keyBy(integrationSettingsValuesList, 'locationId') as Record<
      string,
      IntegrationSettingsValues
    >;
    const integrationSettingsValuesListByAccount = keyBy(integrationSettingsValuesList, 'accountId') as Record<
      string,
      IntegrationSettingsValues
    >;

    const userLevelTableData = dealerContacts.map(user =>
      getUserIntegrationSettingsTableData(user, integrationSettingsValuesListByUser[user.id])
    );
    const locationLevelTableData = dealerLocations.map(location =>
      getLocationIntegrationSettingsTableData(location, integrationSettingsValuesListByLocation[location.locationId!])
    );
    const accountLevelTableData = getAccountIntegrationSettingsTableData(
      { id: dealerAccountId, name: dealerAccountName },
      integrationSettingsValuesListByAccount[dealerAccountId]
    );

    const tableDataByLevel = {
      [IntegrationSettingsLevel.User]: userLevelTableData,
      [IntegrationSettingsLevel.Location]: locationLevelTableData,
      [IntegrationSettingsLevel.Account]: [accountLevelTableData],
    };
    const formData = getIntegrationSettingsDialogFormData(tableDataByLevel);

    const integrationSettingsByLevel = groupBy(integrationSettings.integrationSettings, 'level');
    const tabs = (Object.keys(integrationSettingsByLevel) as IntegrationSettingsTabs[]).map(level => {
      return {
        label: level,
        value: level,
        isVisible: true,
        component: tabComponentByLevel[level]
          ? React.createElement(tabComponentByLevel[level], {
              integrationSettings: integrationSettingsByLevel[level],
              tableData: tableDataByLevel[level],
              vendorProfile,
            })
          : null,
      };
    });

    setTabs(tabs);
    setFormData(formData);
  }, [integrationSettingsData, integrationSettingsValuesListData]);

  return {
    tabs,
    formData,
    isFetching: isISLoading || isISVLoading,
  };
}
